import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchGalleries } from '../lib/cms';
import Lightbox from '../components/Lightbox';

export default function Gallery() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [galleries, setGalleries] = useState([]);
  const [activeGallery, setActiveGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    async function loadData() {
      const data = await fetchGalleries();
      setGalleries(data);

      if (slug) {
        const found = data.find(g => g.slug === slug);
        if (found) {
          setActiveGallery(found);
        } else {
          navigate('/404');
        }
      }
      setLoading(false);
    }
    loadData();
  }, [slug, navigate]);

  const handleOpenLightbox = (index) => {
    setLightboxIndex(index);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <i className="fa-solid fa-spinner animate-spin text-3xl text-brand-oak" />
      </div>
    );
  }

  // If a specific gallery is selected
  if (activeGallery) {
    return (
      <div className="py-20 bg-black animate-fade-in">
        <Helmet>
          <title>{`${activeGallery.title} – Heine Design`}</title>
          <meta name="description" content={activeGallery.description?.replace(/<[^>]*>/g, '') || ''} />
        </Helmet>

        <div className="max-w-7xl mx-auto px-6">
          {/* Back to galleries */}
          <div className="flex items-center gap-2 text-xs text-brand-charcoal uppercase tracking-widest mb-10">
            <Link to="/" className="hover:text-brand-oak transition-colors">Home</Link>
            <span>/</span>
            <Link to="/galleri" className="hover:text-brand-oak transition-colors">Gallerier</Link>
            <span>/</span>
            <span className="text-white font-medium">{activeGallery.title}</span>
          </div>

          {/* Header */}
          <div className="space-y-4 mb-12">
            <span className="text-xs tracking-[0.25em] uppercase text-brand-oak font-semibold block">
              {activeGallery.category}
            </span>
            <h1 className="text-3xl md:text-4xl font-light tracking-tight text-white uppercase">
              {activeGallery.title}
            </h1>
            <div className="h-[1px] w-20 bg-brand-oak" />
            {activeGallery.description && (
              <div
                className="text-sm text-brand-charcoal leading-relaxed font-light max-w-2xl pt-4"
                dangerouslySetInnerHTML={{ __html: activeGallery.description }}
              />
            )}
          </div>

          {/* Images Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {activeGallery.images?.map((img, idx) => (
              <div
                key={idx}
                onClick={() => handleOpenLightbox(idx)}
                className="group relative aspect-square bg-brand-light overflow-hidden rounded-sm cursor-pointer border border-brand-gray"
              >
                <img
                  src={img}
                  alt={`${activeGallery.title} - Billede ${idx + 1}`}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center text-black shadow-lg">
                    <i className="fa-solid fa-magnifying-glass-plus text-lg" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Lightbox */}
        {lightboxIndex !== null && (
          <Lightbox
            images={activeGallery.images}
            currentIndex={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
            onPrev={() => setLightboxIndex((prev) => (prev - 1 + activeGallery.images.length) % activeGallery.images.length)}
            onNext={() => setLightboxIndex((prev) => (prev + 1) % activeGallery.images.length)}
          />
        )}
      </div>
    );
  }

  // List of all galleries
  return (
    <div className="py-20 bg-black animate-fade-in">
      <Helmet>
        <title>Gallerier – HEINE DESIGN</title>
        <meta name="description" content="Udforsk vores billedgallerier, der viser vores håndlavede danske designmøbler og processen på vores værksted." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="space-y-4 mb-16 text-center">
          <span className="text-xs tracking-[0.25em] uppercase text-brand-oak font-semibold block">
            Inspiration
          </span>
          <h1 className="text-3xl md:text-4xl font-light tracking-tight text-white uppercase">
            Billedgallerier
          </h1>
          <div className="h-[1px] w-20 bg-brand-oak mx-auto" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {galleries.map((gallery) => (
            <Link
              key={gallery.id}
              to={`/galleri/${gallery.slug}`}
              className="group flex flex-col bg-brand-light border border-brand-gray rounded-sm overflow-hidden transition-all duration-300 hover:shadow-black/40 hover:shadow-xl hover:-translate-y-1"
            >
              <div className="aspect-[16/10] overflow-hidden bg-black relative">
                <img
                  src={gallery.images?.[0]}
                  alt={gallery.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors duration-300" />
              </div>
              <div className="p-8 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-light tracking-widest text-white uppercase group-hover:text-brand-oak transition-colors duration-300">
                    {gallery.title}
                  </h3>
                  <span className="text-[10px] tracking-widest uppercase text-brand-oak font-semibold">
                    {gallery.category}
                  </span>
                </div>
                {gallery.description && (
                  <div
                    className="text-xs text-brand-charcoal leading-relaxed font-light line-clamp-2"
                    dangerouslySetInnerHTML={{ __html: gallery.description }}
                  />
                )}
                <span className="text-xs text-white font-medium uppercase tracking-wider inline-flex items-center gap-2 pt-2 group-hover:text-brand-oak transition-colors">
                  <span>Se galleri</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
